// ============================================================================
// api_deviceSignaling.ts
// Signalisation WebSocket côté device
// v2.5 — Ajout support intercomm (intercomm_regie_to_device,
//         intercomm_device_to_regie, message 'intercomm')
// ============================================================================

import { DeviceSignalingConfig, DeviceSignalingRefs, DeviceSignalingCallbacks } from '../../dataTypes/liveStreamManagment/types_callbacks';
import { parseSdpCodec, getSupportedVideoCodecNames } from '../../dataTypes/liveStreamManagment/utils_sdp';

// Nombre de tentatives de handshake WS consécutives (jamais de onopen) avant d'abandonner
// et de rendre la main à l'utilisateur — ex. chaîne éteinte côté régie (404 HAProxy).
const MAX_HANDSHAKE_ATTEMPTS = 3;

export const connectDeviceToSignaling = (
  config: DeviceSignalingConfig,
  refs: DeviceSignalingRefs,
  callbacks: DeviceSignalingCallbacks,
  attempt: number = 1,
): void => {
  const { serverUrl, channelUUID, deviceId, password, accessToken } = config;
  const wsUrl = `wss://${serverUrl}/${channelUUID}/webrtc/signaling/device/${deviceId}`;
  console.log(`[DEVICE-SIGNALING] 🔌 Connexion WS → ${wsUrl} (tentative ${attempt}/${MAX_HANDSHAKE_ATTEMPTS})`);

  const ws = new WebSocket(wsUrl);
  refs.wsRef.current = ws;
  let didOpen = false;

  ws.onopen = () => {
    didOpen = true;
    callbacks.onSignalingStatusChange?.(true);
    console.log(`[DEVICE-SIGNALING] ✅ WS ouvert — envoi auth pour ${deviceId}`);
    callbacks.addSdpLog({ direction: 'sent', type: 'event', label: `🔌 WS ouvert — auth: ${deviceId}` });
    // Mode "uuid" : c'est le token du lien qui authentifie, pas de mot de passe.
    ws.send(JSON.stringify({
      type: 'auth', device_id: deviceId,
      ...(accessToken ? { access_token: accessToken } : { password }),
      supported_codecs: getSupportedVideoCodecNames(),
    }));
  };

  ws.onmessage = async (e) => {
    try {
      const msg = JSON.parse(e.data);
      if (msg.type !== 'pong') {
        console.log(`[DEVICE-SIGNALING] ↓ message reçu: type=${msg.type}`);
      }
      await handleDeviceSignalingMessage(JSON.parse(e.data), ws, callbacks);
    }
    catch (err) { console.error('[DEVICE-SIGNALING] Erreur parsing message:', err); }
  };

  ws.onerror = (e) => {
    console.error('[DEVICE-SIGNALING] ❌ WS error:', e);
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ WS device error' });
  };

  ws.onclose = (e) => {
    console.warn(`[DEVICE-SIGNALING] 🔌 WS fermé (code=${e.code})`);
    callbacks.onSignalingStatusChange?.(false);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔌 WS fermé' });

    if (!didOpen) {
      // Le handshake WS n'a jamais abouti (jamais eu de onopen) : le navigateur ne donne
      // pas accès au status HTTP du refus, donc on rejoue la même requête en HTTP simple
      // sur la même URL pour diagnostiquer. HAProxy applique le même garde-fou
      // "channel_active" à toute requête HTTP sur ce chemin, WS ou pas — un 404 avec ce
      // corps signifie que la chaîne est inconnue/éteinte côté régie.
      //
      // On limite les tentatives (MAX_HANDSHAKE_ATTEMPTS) : au-delà, on abandonne et on
      // rend la main à l'utilisateur plutôt que de tourner indéfiniment en silence.
      fetch(wsUrl.replace(/^wss:/, 'https:'), { cache: 'no-store' })
        .then(async (res) => {
          if (res.status !== 404) return undefined;
          const body = await res.json().catch(() => null);
          return body?.error as string | undefined;
        })
        .catch(() => undefined)
        .then((diagnosedReason) => {
          if (!refs.shouldReconnectRef.current) return;

          if (attempt >= MAX_HANDSHAKE_ATTEMPTS) {
            callbacks.addSdpLog({ direction: 'info', type: 'event', label: `❌ Abandon après ${attempt} tentatives` });
            callbacks.onConnectionFailed?.(diagnosedReason);
            return;
          }

          callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔄 Reconnexion dans 5s (tentative ${attempt + 1}/${MAX_HANDSHAKE_ATTEMPTS})…` });
          setTimeout(() => {
            if (refs.shouldReconnectRef.current) connectDeviceToSignaling(config, refs, callbacks, attempt + 1);
          }, 5000);
        });
      return;
    }

    // La connexion avait déjà été établie au moins une fois (session en cours coupée,
    // ex. réseau) : on retente indéfiniment tant que l'utilisateur ne s'est pas déconnecté,
    // le compteur de tentatives repart à zéro puisqu'il ne s'agit plus d'un handshake refusé.
    if (refs.shouldReconnectRef.current) {
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🔄 Reconnexion dans 5s…' });
      setTimeout(() => {
        if (refs.shouldReconnectRef.current) connectDeviceToSignaling(config, refs, callbacks, 1);
      }, 5000);
    }
  };
};

export const closeDeviceSignaling = (
  refs: Pick<DeviceSignalingRefs, 'wsRef'>
): void => {
  if (refs.wsRef.current) {
    refs.wsRef.current.onclose = null;
    refs.wsRef.current.close();
    refs.wsRef.current = null;
  }
};

// ─── Envoi WS ─────────────────────────────────────────────────────────────────

const wsSend = (wsRef: React.MutableRefObject<WebSocket | null>, payload: object): void => {
  if (wsRef.current?.readyState === WebSocket.OPEN)
    wsRef.current.send(JSON.stringify(payload));
};

export const sendDeviceIceCandidate = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  candidate: RTCIceCandidate
): void => wsSend(wsRef, { type: 'ice-candidate', target: 'control', payload: candidate });

export const sendDeviceOffer = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  offer: RTCSessionDescriptionInit
): void => wsSend(wsRef, { type: 'offer', target: 'control', payload: offer, supported_codecs: getSupportedVideoCodecNames() });

export const sendDeviceAnswer = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  answer: RTCSessionDescriptionInit
): void => wsSend(wsRef, { type: 'answer', target: 'control', payload: answer, supported_codecs: getSupportedVideoCodecNames() });

/**
 * Demande à la régie de passer une source en final, depuis le mini-deck du mode
 * Réalisateur. `sourceId` provient tel quel du dernier `director_deck` reçu.
 *
 * Best effort et sans accusé : la confirmation, c'est le `director_deck` suivant,
 * où `finalId` aura changé. La régie refuse la demande si ce device ne porte plus
 * le rôle — le bouton restera alors simplement sans effet visible.
 */
/**
 * Rapporte à la régie la qualité du lien MONTANT, que le device est seul à
 * pouvoir mesurer : en mode SFU la régie tire le flux depuis MediaMTX et ne voit
 * rien du trajet device → SFU.
 *
 * Best effort, sans accusé : une mesure perdue est remplacée par la suivante.
 */
export const sendNetworkQuality = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  level: number,
  sample: unknown
): void => wsSend(wsRef, { type: 'net_quality', target: 'control', level, sample });

export const sendDirectorTake = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  sourceId: string
): void => wsSend(wsRef, { type: 'director_take', target: 'control', sourceId });

// ─── Dispatch messages entrants ───────────────────────────────────────────────

const handleDeviceSignalingMessage = async (
  msg: any,
  ws: WebSocket,
  callbacks: DeviceSignalingCallbacks
): Promise<void> => {
  switch (msg.type) {
    case 'auth_success':
      console.log('[DEVICE-SIGNALING] ✅ auth_success reçu — initialisation device…');
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: '✅ Auth succès' });

      // msg.sfu : présent quand le microservice a créé le path source MediaMTX
      // (transport SFU actif) — contient whip_url et program_whep_url.
      await callbacks.onAuthSuccess(msg.sfu);

      if (ws.readyState === WebSocket.OPEN) {
        console.log('[DEVICE-SIGNALING] ✅ ready envoyé au serveur');
        callbacks.addSdpLog({ direction: 'sent', type: 'event', label: '✅ ready envoyé — régie peut envoyer l\'Offer' });
        ws.send(JSON.stringify({ type: 'ready' }));
      }
      break;

    case 'auth_error': {
      const isDoubleConn = msg.reason === 'already_connected';
      console.warn(`[DEVICE-SIGNALING] ❌ auth_error: ${msg.reason}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'error',
        label: isDoubleConn
          ? '❌ Auth error — identifiant déjà utilisé'
          : '❌ Auth error — identifiant ou mot de passe incorrect',
      });
      callbacks.onAuthError(msg.reason);
      break;
    }

    case 'offer':
      if (msg.stream_id === 'return') {
        console.log('[DEVICE-SIGNALING] ↓ offer RETOUR reçue ← régie');
        callbacks.addSdpLog({
          direction: 'received', type: 'offer', label: 'Offer retour ← régie',
          sdp: msg.payload?.sdp,
          codec: msg.payload?.sdp ? parseSdpCodec(msg.payload.sdp) : undefined,
        });
        await callbacks.onReturnOffer?.(msg.from, msg.payload);

      } else if (msg.stream_id === 'intercomm_regie_to_device') {
        console.log('[DEVICE-SIGNALING] ↓ offer INTERCOMM régie→device reçue ← régie');
        callbacks.addSdpLog({
          direction: 'received', type: 'offer',
          label: '🎙 Offer intercomm régie→device ← régie',
          sdp: msg.payload?.sdp,
          codec: msg.payload?.sdp ? parseSdpCodec(msg.payload.sdp) : undefined,
        });
        await callbacks.onIntercommOffer?.('intercomm_regie_to_device', msg.payload);

      } else if (msg.stream_id === 'intercomm_device_to_regie') {
        console.log('[DEVICE-SIGNALING] ↓ offer INTERCOMM device→régie reçue ← régie');
        callbacks.addSdpLog({
          direction: 'received', type: 'offer',
          label: '🎙 Offer intercomm device→régie ← régie',
          sdp: msg.payload?.sdp,
          codec: msg.payload?.sdp ? parseSdpCodec(msg.payload.sdp) : undefined,
        });
        await callbacks.onIntercommOffer?.('intercomm_device_to_regie', msg.payload);

      } else {
        console.log('[DEVICE-SIGNALING] ↓ offer reçue ← régie');
        callbacks.addSdpLog({
          direction: 'received', type: 'offer', label: 'Offer ← régie',
          sdp: msg.payload?.sdp,
          codec: msg.payload?.sdp ? parseSdpCodec(msg.payload.sdp) : undefined,
        });
        await callbacks.onOffer(msg.from, msg.payload, { required_codec: msg.required_codec });
      }
      break;

    case 'answer':
      // Le device n'envoie jamais d'answer au flux retour
      // (c'est lui qui reçoit l'offer retour et envoie l'answer)
      // Ce case reste inchangé pour le flux principal.
      console.log('[DEVICE-SIGNALING] ↓ answer reçue ← régie');
      callbacks.addSdpLog({
        direction: 'received', type: 'answer', label: 'Answer ← régie',
        sdp: msg.payload?.sdp,
        codec: msg.payload?.sdp ? parseSdpCodec(msg.payload.sdp) : undefined,
      });
      await callbacks.onAnswer(msg.from, msg.payload);
      break;

    case 'ice-candidate':
      if (msg.stream_id === 'return') {
        await callbacks.onReturnIceCandidate?.(msg.from, msg.payload);

      } else if (
        msg.stream_id === 'intercomm_regie_to_device' ||
        msg.stream_id === 'intercomm_device_to_regie'
      ) {
        await callbacks.onIntercommIce?.(msg.stream_id, msg.payload);

      } else {
        await callbacks.onIceCandidate(msg.from, msg.payload);
      }
      break;

    // ── INTERCOMM : la régie active/désactive l'intercomm vers ce device ──────
    case 'intercomm':
      console.log(`[DEVICE-SIGNALING] 🎙 intercomm: active=${msg.active}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `🎙 Intercomm régie: ${msg.active ? 'ON' : 'OFF'}`,
      });
      callbacks.onIntercomm?.(msg.active);
      break;

    // ── ON AIR : la régie indique si ce device est le flux final (à l'antenne) ─
    case 'on_air':
      console.log(`[DEVICE-SIGNALING] 📡 on_air: active=${msg.active}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `📡 Flux final régie: ${msg.active ? 'ON' : 'OFF'}`,
      });
      callbacks.onOnAir?.(msg.active);
      break;

    // ── RÉALISATEUR : la régie donne/retire le rôle à ce device ───────────────
    case 'director':
      console.log(`[DEVICE-SIGNALING] 🎬 director: active=${msg.active}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `🎬 Mode réalisateur: ${msg.active ? 'ON' : 'OFF'}`,
      });
      callbacks.onDirector?.(msg.active);
      break;

    // ── DECK : état du pupitre régie, pour le mini-deck du Réalisateur ────────
    case 'director_deck':
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `🎬 Deck régie: ${msg.sources?.length ?? 0} source(s), final=${msg.finalId ?? '—'}`,
      });
      callbacks.onDirectorDeck?.(msg.sources ?? [], msg.finalId ?? null);
      break;

    case 'device_rejected':
      console.warn('[DEVICE-SIGNALING] 🚫 device_rejected', msg);
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: '❌ Device rejeté', raw: msg });
      callbacks.onDeviceRejected(msg);
      break;

    case 'force_disconnect':
      console.warn('[DEVICE-SIGNALING] 🔌 force_disconnect reçu ← régie');
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: '🔌 Déconnecté par la régie' });
      callbacks.onForceDisconnect?.();
      break;

    case 'pong':
      // heartbeat silencieux
      break;

    default:
      console.warn(`[DEVICE-SIGNALING] Message inconnu: type=${msg.type}`, msg);
  }
};